import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { VisitorConsentContent } from "../../../features/VisitorConsent/visitor-consent-content.context";
import {
  fetchVisitorConsentPageContent,
  publishVisitorConsentPageDraft,
  saveVisitorConsentPageDraft
} from "./visit-consent-content.api";

export const visitorConsentContentQueryKey = ["visitor-consent", "content"] as const;

export function useRemoteVisitorConsentContent() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const contentQuery = useQuery(visitorConsentContentQueryKey, async () => {
    const { data } = await fetchVisitorConsentPageContent();
    return data;
  });

  const invalidateContent = () => queryClient.invalidateQueries(visitorConsentContentQueryKey);

  const saveDraftMutation = useMutation({
    mutationFn: async (draft: VisitorConsentContent) => {
      await saveVisitorConsentPageDraft(draft);
    },
    onError: () => {
      enqueueSnackbar(t("_visitorConsentContent_saveError"), { variant: "error" });
    }
  });

  const publishMutation = useMutation({
    mutationFn: async (draft: VisitorConsentContent) => {
      await saveVisitorConsentPageDraft(draft);
      await publishVisitorConsentPageDraft();
    },
    onSuccess: async () => {
      await invalidateContent();
      enqueueSnackbar(t("_visitorConsentContent_publishSuccess"), { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar(t("_visitorConsentContent_publishError"), { variant: "error" });
    }
  });

  return {
    contentQuery,
    saveDraftMutation,
    publishMutation
  };
}
